import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import { ResetPasswordSchema, TResetPassword } from '@onesource/schemas';
import { useSetPasswordWithTokenMutation, useValidateResetTokenQuery } from '@onesource/services';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

export interface ResetPasswordWithTokenFormProps {
  /** The reset token from the URL query string */
  token: string;
  /** URL for the "Back to Login" link (default: '/auth/login') */
  loginUrl?: string;
  /** Custom title (default: "Reset Password") */
  title?: string;
  /** Callback when password is reset successfully */
  onSuccess?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Custom styles for the form card */
  sx?: SxProps<Theme>;
}

export const ResetPasswordWithTokenForm: FC<ResetPasswordWithTokenFormProps> = ({
  token,
  loginUrl = '/auth/login',
  title = 'Reset Password',
  onSuccess,
  onError,
  sx,
}) => {
  const mutation = useSetPasswordWithTokenMutation(token);
  const { data: tokenStatus, isLoading: isValidating, isError } = useValidateResetTokenQuery(token);
  const isTokenInvalid = !token || isError || (tokenStatus && !tokenStatus.valid);

  const { control, handleSubmit } = useForm<TResetPassword>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'all',
  });

  // Redirect to login if token is invalid (but not after a successful reset)
  useEffect(() => {
    if (!isValidating && isTokenInvalid && !mutation.isSuccess && loginUrl) {
      const timer = setTimeout(() => {
        window.location.href = loginUrl;
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isValidating, isTokenInvalid, mutation.isSuccess, loginUrl]);

  // Success state — must be checked before isTokenInvalid since the token is consumed after reset
  if (mutation.isSuccess) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 4,
          ...sx,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'success.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{ width: 36, height: 36, color: 'success.main' }}
          >
            <polyline points="20 6 9 17 4 12" />
          </Box>
        </Box>
        <Typography variant="h5" fontWeight={700}>
          Password Reset Successfully
        </Typography>
        <Typography color="text.secondary" variant="body2" maxWidth={320}>
          Your password has been updated. You can now log in with your new credentials.
        </Typography>
        <Button variant="contained" href={loginUrl} sx={{ mt: 1, px: 4, borderRadius: 2 }}>
          Go to Login
        </Button>
      </Box>
    );
  }

  // Loading state while validating token
  if (token && isValidating) {
    return (
      <Box sx={{ textAlign: 'center', ...sx }}>
        <Typography variant="h5" mb={2}>
          Validating Reset Link...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  // Invalid or expired token state
  if (isTokenInvalid) {
    return (
      <Box sx={{ textAlign: 'center', ...sx }}>
        <Typography variant="h5" color="error" mb={2}>
          Invalid or Expired Reset Link
        </Typography>
        <Typography color="text.secondary" mb={3}>
          This password reset link is invalid or has expired. You will be redirected to the login page shortly, where
          you can request a new one.
        </Typography>
        <Button variant="contained" href={loginUrl}>
          Back to Login
        </Button>
      </Box>
    );
  }

  const onSubmit = handleSubmit(({ password }) => {
    mutation.mutate(
      { token, password },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: err => onError?.(err instanceof Error ? err : new Error(String(err))),
      },
    );
  });

  return (
    <Box sx={sx}>
      <Typography variant="h5" textAlign="center" mb={1}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Enter your new password below.
      </Typography>

      <form onSubmit={onSubmit}>
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              type="password"
              label="New Password"
              error={!!fieldState.error}
              helperText={fieldState.error?.message || 'Minimum 8 characters'}
              margin="normal"
              autoFocus
            />
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              type="password"
              label="Confirm Password"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              margin="normal"
            />
          )}
        />

        {mutation.isError && (
          <Typography color="error" sx={{ mt: 1 }} variant="body2">
            {mutation.error instanceof Error ? mutation.error.message : 'Failed to reset password'}
          </Typography>
        )}

        <Button type="submit" variant="contained" fullWidth disabled={mutation.isPending} sx={{ mt: 3 }}>
          {mutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
        </Button>
      </form>

      <Box mt={2} textAlign="center">
        <Link href={loginUrl} sx={{ fontWeight: 600, fontSize: '14px' }}>
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};
